/**
 * Data display blocks: stats, list, table, gallery
 * Blocks that display structured data.
 */

import { registerBlockRenderer, getBlockRendererContext } from './registry';
import { escapeHtml, createBlockWrapper, handleImageError } from './utils';
import { openLightbox } from '../lightbox';
import { iconCheck, iconChevronLeft, iconChevronRight, renderIcon } from '../icons';
import type { StatsData, ListData, TableData, GalleryData, GalleryItem } from './types';

// =============================================================================
// STATS BLOCK
// =============================================================================

registerBlockRenderer('stats', (data: unknown, container: HTMLElement) => {
  const statsData = data as StatsData;
  const items = statsData.items ?? [];

  const wrapper = createBlockWrapper('stats');

  const grid = document.createElement('div');
  grid.className = 'pms-stats-grid';

  for (const item of items) {
    const statEl = document.createElement('div');
    statEl.className = 'pms-stat';

    let html = '';
    if (item.icon) {
      const iconSvg = renderIcon(item.icon);
      html += `<div class="pms-stat-icon">${iconSvg || ''}</div>`;
    }
    if (item.value !== undefined) {
      html += `<div class="pms-stat-value">${escapeHtml(String(item.value))}</div>`;
    }
    if (item.label) {
      html += `<div class="pms-stat-label">${escapeHtml(item.label)}</div>`;
    }

    statEl.innerHTML = html;
    grid.appendChild(statEl);
  }

  wrapper.appendChild(grid);
  container.appendChild(wrapper);
});

// =============================================================================
// LIST BLOCK
// =============================================================================

registerBlockRenderer('list', (data: unknown, container: HTMLElement) => {
  const listData = data as ListData;
  const items = listData.items ?? [];

  const wrapper = createBlockWrapper('list', 'pms-list-check');

  if (listData.title) {
    const title = document.createElement('div');
    title.className = 'pms-list-title';
    title.textContent = listData.title;
    wrapper.appendChild(title);
  }

  const listEl = document.createElement('ul');
  listEl.className = 'pms-list';

  for (const item of items) {
    const li = document.createElement('li');
    li.className = 'pms-list-item';

    if (typeof item === 'string') {
      li.innerHTML = `<span class="pms-check-icon">${iconCheck()}</span><span class="pms-list-content">${escapeHtml(item)}</span>`;
    } else {
      let html = '';
      if (item.icon) {
        const iconSvg = renderIcon(item.icon);
        html += `<span class="pms-list-icon">${iconSvg || iconCheck()}</span>`;
      } else {
        html += `<span class="pms-check-icon">${iconCheck()}</span>`;
      }
      // Wrap primary + secondary in content container for proper stacking
      html += '<span class="pms-list-content">';
      html += `<span class="pms-list-primary">${escapeHtml(item.primary)}</span>`;
      if (item.secondary) {
        html += `<span class="pms-list-secondary">${escapeHtml(item.secondary)}</span>`;
      }
      html += '</span>';
      li.innerHTML = html;
    }
    listEl.appendChild(li);
  }

  wrapper.appendChild(listEl);
  container.appendChild(wrapper);
});

// =============================================================================
// TABLE BLOCK
// =============================================================================

registerBlockRenderer('table', (data: unknown, container: HTMLElement) => {
  const tableData = data as TableData;

  const wrapper = createBlockWrapper('table');

  if (tableData.title) {
    const title = document.createElement('div');
    title.className = 'pms-table-title';
    title.textContent = tableData.title;
    wrapper.appendChild(title);
  }

  const tableWrapper = document.createElement('div');
  tableWrapper.className = 'pms-table-wrapper';

  const table = document.createElement('table');
  table.className = 'pms-table';

  if (tableData.headers && tableData.headers.length > 0) {
    const thead = document.createElement('thead');
    const tr = document.createElement('tr');
    for (const header of tableData.headers) {
      const th = document.createElement('th');
      th.textContent = header;
      tr.appendChild(th);
    }
    thead.appendChild(tr);
    table.appendChild(thead);
  }

  if (tableData.rows && tableData.rows.length > 0) {
    const tbody = document.createElement('tbody');
    for (const row of tableData.rows) {
      const tr = document.createElement('tr');
      for (const cell of row) {
        const td = document.createElement('td');
        td.textContent = cell;
        tr.appendChild(td);
      }
      tbody.appendChild(tr);
    }
    table.appendChild(tbody);
  }

  tableWrapper.appendChild(table);
  wrapper.appendChild(tableWrapper);
  container.appendChild(wrapper);
});

// =============================================================================
// GALLERY BLOCK - Enhanced with Carousel and Lightbox
// =============================================================================

registerBlockRenderer('gallery', (data: unknown, container: HTMLElement) => {
  const galleryData = data as GalleryData;
  const items = galleryData.items ?? [];

  if (items.length === 0) return;

  const wrapper = createBlockWrapper('gallery');

  // Title
  if (galleryData.title) {
    const title = document.createElement('div');
    title.className = 'pms-gallery-title';
    title.textContent = galleryData.title;
    wrapper.appendChild(title);
  }

  // Determine layout: carousel for 3+ items or explicit setting
  const useCarousel = galleryData.layout === 'carousel' || (galleryData.layout !== 'grid' && items.length >= 3);

  // Lightbox settings (default: enabled)
  const enableLightbox = galleryData.enableLightbox !== false;

  // Open lightbox for an image
  const openImageLightbox = (startIndex: number) => {
    if (!enableLightbox) return;

    const lightboxImages = items.map((item: GalleryItem) => ({
      src: item.fullImage || item.image || '',
      alt: item.label,
      caption: item.caption
    })).filter((img: { src: string }) => img.src);

    if (lightboxImages.length > 0) {
      // Get root element from context for shadow DOM support
      const ctx = getBlockRendererContext();
      openLightbox({ images: lightboxImages, startIndex, rootElement: ctx.rootElement });
    }
  };

  if (useCarousel) {
    // CAROUSEL LAYOUT
    renderCarousel(wrapper, items, galleryData, openImageLightbox);
  } else {
    // GRID LAYOUT
    renderGrid(wrapper, items, enableLightbox, openImageLightbox);
  }

  container.appendChild(wrapper);
});

/**
 * Render gallery as carousel
 */
function renderCarousel(
  wrapper: HTMLElement,
  items: GalleryItem[],
  options: GalleryData,
  openLightbox: (index: number) => void
): void {
  const showIndicators = options.showIndicators !== false;
  const showNavigation = options.showNavigation !== false && items.length > 1;
  const enableLightbox = options.enableLightbox !== false;

  const carousel = document.createElement('div');
  carousel.className = 'pms-gallery-carousel';

  // Track container
  const track = document.createElement('div');
  track.className = 'pms-gallery-track';

  let currentIndex = 0;

  // Create slides
  items.forEach((item, index) => {
    const slide = document.createElement('div');
    slide.className = 'pms-gallery-slide';

    if (item.image) {
      const img = document.createElement('img');
      img.className = 'pms-gallery-slide-image';
      img.src = item.image;
      img.alt = item.label || `Image ${index + 1}`;
      img.loading = index === 0 ? 'eager' : 'lazy';
      img.decoding = 'async';

      if (enableLightbox) {
        img.style.cursor = 'zoom-in';
        img.addEventListener('click', () => openLightbox(index));
      }

      handleImageError(img);
      slide.appendChild(img);
    } else {
      const placeholder = document.createElement('div');
      placeholder.className = 'pms-gallery-slide-placeholder';
      slide.appendChild(placeholder);
    }

    // Optional label overlay
    if (item.label) {
      const label = document.createElement('div');
      label.className = 'pms-gallery-slide-label';
      label.textContent = item.label;
      slide.appendChild(label);
    }

    track.appendChild(slide);
  });

  carousel.appendChild(track);

  // Update carousel position
  const updateCarousel = () => {
    track.style.transform = `translateX(-${currentIndex * 100}%)`;

    // Update indicators
    if (showIndicators) {
      const dots = carousel.querySelectorAll('.pms-gallery-dot');
      dots.forEach((dot, i) => {
        dot.classList.toggle('active', i === currentIndex);
      });
    }

    // Update nav button states
    if (showNavigation) {
      const prevBtn = carousel.querySelector('.pms-gallery-prev') as HTMLButtonElement;
      const nextBtn = carousel.querySelector('.pms-gallery-next') as HTMLButtonElement;
      if (prevBtn) prevBtn.disabled = currentIndex === 0;
      if (nextBtn) nextBtn.disabled = currentIndex === items.length - 1;
    }
  };

  // Navigation buttons
  if (showNavigation) {
    const prevBtn = document.createElement('button');
    prevBtn.className = 'pms-gallery-nav pms-gallery-prev';
    prevBtn.setAttribute('aria-label', 'Previous image');
    prevBtn.innerHTML = iconChevronLeft();
    prevBtn.addEventListener('click', () => {
      if (currentIndex > 0) {
        currentIndex--;
        updateCarousel();
      }
    });

    const nextBtn = document.createElement('button');
    nextBtn.className = 'pms-gallery-nav pms-gallery-next';
    nextBtn.setAttribute('aria-label', 'Next image');
    nextBtn.innerHTML = iconChevronRight();
    nextBtn.addEventListener('click', () => {
      if (currentIndex < items.length - 1) {
        currentIndex++;
        updateCarousel();
      }
    });

    carousel.appendChild(prevBtn);
    carousel.appendChild(nextBtn);
  }

  // Dot indicators
  if (showIndicators && items.length > 1) {
    const indicators = document.createElement('div');
    indicators.className = 'pms-gallery-indicators';

    items.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.className = `pms-gallery-dot ${index === 0 ? 'active' : ''}`;
      dot.setAttribute('aria-label', `Go to image ${index + 1}`);
      dot.addEventListener('click', () => {
        currentIndex = index;
        updateCarousel();
      });
      indicators.appendChild(dot);
    });

    carousel.appendChild(indicators);
  }

  // Touch/swipe support
  let touchStartX = 0;
  let touchEndX = 0;

  carousel.addEventListener('touchstart', (e) => {
    const touch = e.changedTouches[0];
    if (touch) touchStartX = touch.screenX;
  }, { passive: true });

  carousel.addEventListener('touchend', (e) => {
    const touch = e.changedTouches[0];
    if (touch) touchEndX = touch.screenX;
    const diff = touchStartX - touchEndX;

    if (Math.abs(diff) > 50) {
      if (diff > 0 && currentIndex < items.length - 1) {
        currentIndex++;
        updateCarousel();
      } else if (diff < 0 && currentIndex > 0) {
        currentIndex--;
        updateCarousel();
      }
    }
  }, { passive: true });

  // Keyboard navigation when focused
  carousel.setAttribute('tabindex', '0');
  carousel.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft' && currentIndex > 0) {
      currentIndex--;
      updateCarousel();
    } else if (e.key === 'ArrowRight' && currentIndex < items.length - 1) {
      currentIndex++;
      updateCarousel();
    }
  });

  // Initial state
  updateCarousel();

  wrapper.appendChild(carousel);
}

/**
 * Render gallery as grid (original layout)
 */
function renderGrid(
  wrapper: HTMLElement,
  items: GalleryItem[],
  enableLightbox: boolean,
  openLightbox: (index: number) => void
): void {
  const grid = document.createElement('div');
  grid.className = 'pms-gallery-grid';

  items.forEach((item, index) => {
    const galleryItem = document.createElement('div');
    galleryItem.className = 'pms-gallery-item';

    if (item.image) {
      const img = document.createElement('img');
      img.className = 'pms-gallery-image';
      img.src = item.image;
      img.alt = item.label;
      img.loading = 'lazy';
      img.decoding = 'async';

      if (enableLightbox) {
        img.style.cursor = 'zoom-in';
        img.addEventListener('click', () => openLightbox(index));
      }

      handleImageError(img);
      galleryItem.appendChild(img);
    } else {
      const placeholder = document.createElement('div');
      placeholder.className = 'pms-gallery-placeholder';
      galleryItem.appendChild(placeholder);
    }

    const label = document.createElement('div');
    label.className = 'pms-gallery-label';
    label.textContent = item.label;
    galleryItem.appendChild(label);

    if (item.caption) {
      const caption = document.createElement('div');
      caption.className = 'pms-gallery-caption';
      caption.textContent = item.caption;
      galleryItem.appendChild(caption);
    }

    grid.appendChild(galleryItem);
  });

  wrapper.appendChild(grid);
}
